/**
 * TunnelBuilder.jsx
 * 
 * BUCKET 4: New focused roster builder ("The Tunnel")
 * 
 * Key differences from Legacy (GMRosterBuilderV2):
 * - Single slot at a time focus
 * - Progress-first UI with clear completion indicator
 * - Autosave triggers on slot complete, navigation, and beforeunload
 * - Uses canonical rosterHelpers (no duplicated completion logic)
 * 
 * Autosave Triggers (per Bucket 4 dev plan):
 * 1. Immediate save when slot transitions to complete (prop+side+units>0)
 * 2. Debounced save on slot navigation (300ms)
 * 3. Best-effort save on browser beforeunload
 * 4. Save on tab visibility change (hidden)
 * 
 * BSL Compliant: No edge computation, displays server values only.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Check, Lock, AlertCircle } from 'lucide-react';
import debounce from 'lodash/debounce';

// Data hooks
import { useActiveSlate } from '../../hooks/useActiveSlate';
import { buildRosterPayload } from '../../hooks/useGMStrategy';

// Canonical helpers - DO NOT DUPLICATE THIS LOGIC
import { 
  isSlotComplete, 
  countCompletedSlots, 
  calculateTotalUnits,
  isRosterComplete,
  findFirstIncompleteSlot,
  createEmptyRoster
} from '../../utils/rosterHelpers';

// Constants
import { INITIAL_ROSTER, COLORS, POSITION_COLORS } from '../../constants';

// Centralized filtering
import { filterPlayers } from '../../utils/transformers';

// Phase 1A: Live Outcomes polling (for locked state)
import { useLiveOutcomesPolling } from '../../hooks/useLiveOutcomesPolling';

// ═══════════════════════════════════════════════════════════════════════════════
// SUBCOMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Progress indicator showing completed slots and units
 */
const ProgressHeader = ({ completedSlots, totalSlots, usedUnits, requiredUnits }) => {
  const slotsComplete = completedSlots >= totalSlots;
  const unitsComplete = usedUnits === requiredUnits;
  const allComplete = slotsComplete && unitsComplete;
  
  return (
    <div className="bg-white border-b border-slate-200 px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">
          {completedSlots}/{totalSlots} picks complete
        </span>
        <span className={`text-sm font-bold ${unitsComplete ? 'text-green-600' : 'text-slate-500'}`}>
          {usedUnits}/{requiredUnits} units
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-300 ${allComplete ? 'bg-green-500' : 'bg-indigo-500'}`}
          style={{ width: `${(completedSlots / totalSlots) * 100}%` }}
        />
      </div>
      
      {/* Units sub-bar */}
      <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-1">
        <div 
          className={`h-full rounded-full transition-all duration-300 ${unitsComplete ? 'bg-green-400' : 'bg-indigo-300'}`}
          style={{ width: `${Math.min((usedUnits / requiredUnits) * 100, 100)}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Slot navigation dots
 */
const SlotNavDots = ({ roster, currentIndex, onNavigate }) => {
  return (
    <div className="flex items-center justify-center gap-2 py-3">
      {roster.map((slot, index) => {
        const complete = isSlotComplete(slot);
        const isCurrent = index === currentIndex;
        
        return (
          <button
            key={slot.slot}
            onClick={() => onNavigate(index)}
            className={`
              w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
              transition-all duration-200
              ${isCurrent 
                ? 'bg-indigo-600 text-white scale-110 shadow-lg' 
                : complete 
                  ? 'bg-green-500 text-white' 
                  : 'bg-slate-200 text-slate-500'
              }
            `}
          >
            {complete && !isCurrent ? <Check size={14} /> : slot.slot}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Current slot builder view
 */
const SlotBuilder = ({ 
  slot, 
  players, 
  filters,
  onFiltersChange,
  onSelectPlayer,
  onSelectProp,
  onUnitsChange,
  onClear,
  maxUnits 
}) => {
  // Determine initial view based on slot state
  const getInitialView = () => {
    if (!slot.player) return 'players';
    if (!slot.prop || !slot.side) return 'props';
    return 'summary';
  };
  
  const [localView, setLocalView] = useState(getInitialView);
  const posColor = POSITION_COLORS[slot.label.replace(/[0-9]/g, '')] || COLORS.slate500;
  
  // Track previous slot number to detect slot navigation vs prop selection
  const prevSlotRef = React.useRef(slot.slot);
  
  // Update view when slot NAVIGATION happens (slot number changes)
  useEffect(() => {
    if (slot.slot !== prevSlotRef.current) {
      // Slot changed - reset view based on new slot's state
      prevSlotRef.current = slot.slot;
      if (!slot.player) {
        setLocalView('players');
      } else if (!slot.prop || !slot.side) {
        setLocalView('props');
      } else {
        setLocalView('summary');
      }
    }
  }, [slot.slot, slot.player, slot.prop, slot.side]);
  
  // Handle prop selection - transition to summary
  const handlePropSelect = (prop, side) => {
    onSelectProp(prop, side);
    setLocalView('summary');
  };
  
  // Summary view when slot is complete (player + prop + side)
  if (localView === 'summary' && slot.player && slot.prop && slot.side) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-xl shadow-sm border-2 p-4" style={{ borderColor: posColor + '40' }}>
          <div className="flex items-center justify-between mb-3">
            <span 
              className="text-xs font-bold px-2 py-1 rounded-full text-white"
              style={{ backgroundColor: posColor }}
            >
              {slot.label}
            </span>
            <button 
              onClick={onClear}
              className="text-xs text-red-500 font-medium"
            >
              Clear
            </button>
          </div>
          
          <h3 className="text-lg font-bold text-slate-900">{slot.player.name}</h3>
          <p className="text-sm text-slate-500 mb-3">{slot.player.team} • {slot.player.pos}</p>
          
          {slot.prop && (
            <div className="bg-slate-50 rounded-lg p-3 mb-3">
              <p className="text-sm font-medium text-slate-700">
                {slot.side} {slot.prop.line} {slot.prop.stat}
              </p>
            </div>
          )}
          
          {/* Units slider */}
          <div className="mt-4">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">Units</span>
              <span className="text-lg font-bold text-indigo-600">{slot.units || 0}u</span>
            </div>
            <input
              type="range"
              min={1}
              max={Math.min(50, maxUnits)}
              value={slot.units || 10}
              onChange={(e) => onUnitsChange(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>1</span>
              <span>{Math.min(50, maxUnits)}</span>
            </div>
          </div>
          
          <button
            onClick={() => setLocalView('players')}
            className="w-full mt-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg"
          >
            Change Player
          </button>
        </div>
      </div>
    );
  }
  
  // Props view when player selected but no prop
  if (localView === 'props' && slot.player) {
    const playerProps = slot.player.props || [];
    
    return (
      <div className="p-4">
        <button 
          onClick={() => setLocalView('players')}
          className="flex items-center gap-1 text-sm text-slate-500 mb-3"
        >
          <ChevronLeft size={16} /> Back to players
        </button>
        
        <h3 className="text-lg font-bold text-slate-900 mb-1">{slot.player.name}</h3>
        <p className="text-sm text-slate-500 mb-4">Select a prop</p>
        
        <div className="space-y-2">
          {playerProps.map((prop, idx) => (
            <div key={prop.id || idx} className="bg-white rounded-lg shadow-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-900">{prop.stat}</span>
                <span className="text-sm text-slate-500">Line: {prop.line}</span>
              </div>
              
              {prop.playerAvg && (
                <p className="text-xs text-slate-400 mb-2">
                  Avg: {prop.playerAvg} ({prop.edge >= 0 ? '+' : ''}{prop.edge?.toFixed(1) || '0'})
                </p>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => handlePropSelect(prop, 'OVER')}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all
                    ${slot.prop?.id === prop.id && slot.side === 'OVER'
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-green-50'
                    }`}
                >
                  OVER
                </button>
                <button
                  onClick={() => handlePropSelect(prop, 'UNDER')}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all
                    ${slot.prop?.id === prop.id && slot.side === 'UNDER'
                      ? 'bg-red-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-red-50'
                    }`}
                >
                  UNDER
                </button>
              </div>
            </div>
          ))}
          
          {playerProps.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              No props available for this player
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Players list view
  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-4 bg-white border-b">
        <input
          type="text"
          placeholder="Search players..."
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg text-sm"
        />
      </div>
      
      {/* Player list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {players.map(player => (
          <button
            key={player.id}
            onClick={() => {
              onSelectPlayer(player);
              setLocalView('props');
            }}
            className="w-full bg-white rounded-lg shadow-sm p-3 text-left hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-slate-900">{player.name}</h4>
                <p className="text-sm text-slate-500">{player.team} • {player.pos}</p>
              </div>
              <ChevronRight size={20} className="text-slate-400" />
            </div>
          </button>
        ))}
        
        {players.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            No players found
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN TUNNEL BUILDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const TunnelBuilder = ({
  pool,
  initialRoster,
  isLocked = false,
  saveEntry,
  lockEntry,
  completeEntry,  // New: Bucket 4 complete endpoint
  onBack,
  onSuccess,
  showToast
}) => {
  // ===== STATE =====
  const [roster, setRoster] = useState(initialRoster || INITIAL_ROSTER);
  const [currentSlotIndex, setCurrentSlotIndex] = useState(0);
  const [filters, setFilters] = useState({ search: '', position: 'ALL' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // Refs for autosave
  const lastSavedRoster = useRef(null);
  const saveInProgress = useRef(false);
  
  // ===== DATA HOOKS =====
  const poolId = pool?.pool_id || pool?.id;
  const userId = localStorage.getItem('userId') || 'test_user_001';
  
  const { 
    players, 
    playersForSlot, 
    loading: slateLoading, 
    error: slateError 
  } = useActiveSlate(poolId);
  
  // Live outcomes for locked state
  const {
    roster: liveRoster,
    score: liveScore,
    resolvedCount,
    lastUpdated
  } = useLiveOutcomesPolling(isLocked ? poolId : undefined, isLocked ? userId : undefined);
  
  // Use live roster when locked
  const displayRoster = isLocked && liveRoster?.length > 0 ? liveRoster : roster;
  
  // ===== HYDRATE FROM INITIAL ROSTER (MOUNT ONLY) =====
  const hasHydrated = useRef(false);
  useEffect(() => {
    // Only hydrate once on mount
    if (hasHydrated.current) return;
    
    if (initialRoster?.length > 0 && initialRoster.some(s => s.player)) {
      hasHydrated.current = true;
      setRoster(initialRoster);
      // Find first incomplete slot
      const firstIncomplete = findFirstIncompleteSlot(initialRoster);
      if (firstIncomplete >= 0) {
        setCurrentSlotIndex(firstIncomplete);
      }
    }
  }, [initialRoster]);
  
  // ===== DERIVED VALUES (using canonical helpers) =====
  const requiredSlots = 7;
  const requiredUnits = 100;
  const completedSlots = countCompletedSlots(roster);
  const usedUnits = calculateTotalUnits(roster);
  const allComplete = isRosterComplete(roster, requiredSlots, requiredUnits);
  const currentSlot = roster[currentSlotIndex];
  
  // Filter players for current slot
  const filteredPlayers = useMemo(() => {
    const basePlayers = currentSlot 
      ? playersForSlot(currentSlot.label) 
      : players;
    return filterPlayers(basePlayers, filters);
  }, [players, currentSlot, filters, playersForSlot]);
  
  // Max units for current slot
  const unitsOnOtherSlots = roster.reduce((sum, s, i) => 
    i === currentSlotIndex ? sum : sum + (s.units || 0), 0
  );
  const maxUnitsForSlot = Math.min(50, requiredUnits - unitsOnOtherSlots);
  
  // ===== AUTOSAVE LOGIC =====
  
  const doSave = useCallback(async (rosterToSave) => {
    if (!saveEntry || saveInProgress.current || isLocked) return;
    
    // Check if roster actually changed
    const rosterJson = JSON.stringify(rosterToSave);
    if (rosterJson === JSON.stringify(lastSavedRoster.current)) return;
    
    saveInProgress.current = true;
    setSaveError(null);
    
    try {
      const payload = buildRosterPayload(rosterToSave);
      const result = await saveEntry(payload);
      
      if (result?.success) {
        lastSavedRoster.current = rosterToSave;
      } else if (result?.errors?.length > 0) {
        setSaveError(result.errors[0]?.message || 'Save failed');
        showToast?.(result.errors[0]?.message || 'Save failed', 'error');
      }
    } catch (err) {
      console.error('[TunnelBuilder] Save error:', err);
      setSaveError('Failed to save');
    } finally {
      saveInProgress.current = false;
    }
  }, [saveEntry, isLocked, showToast]);
  
  // Debounced save for navigation
  const debouncedSave = useMemo(
    () => debounce((r) => doSave(r), 300),
    [doSave]
  );
  
  // AUTOSAVE TRIGGER 1: When slot becomes complete
  useEffect(() => {
    if (isSlotComplete(currentSlot) && !isLocked) {
      doSave(roster);
    }
  }, [currentSlot?.prop?.id, currentSlot?.side, currentSlot?.units]);
  
  // AUTOSAVE TRIGGER 3: Browser beforeunload
  useEffect(() => {
    const handleUnload = () => {
      if (isLocked) return;
      // Best-effort save using sendBeacon
      try {
        const payload = buildRosterPayload(roster);
        navigator.sendBeacon?.(
          `/api/v1/pools/${poolId}/entry/save`,
          JSON.stringify(payload)
        );
      } catch (e) {
        console.warn('[TunnelBuilder] Beacon save failed:', e);
      }
    };
    
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [roster, poolId, isLocked]);
  
  // AUTOSAVE TRIGGER 4: Tab visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && !isLocked) {
        doSave(roster);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [roster, doSave, isLocked]);
  
  // ===== HANDLERS =====
  
  const handleSlotNavigation = useCallback((newIndex) => {
    // AUTOSAVE TRIGGER 2: Debounced save on navigation
    debouncedSave(roster);
    setCurrentSlotIndex(newIndex);
  }, [roster, debouncedSave]);
  
  const handleSelectPlayer = useCallback((player) => {
    const updatedRoster = roster.map((s, i) => 
      i === currentSlotIndex 
        ? { ...s, player, prop: null, side: null, units: s.units || 10 }
        : s
    );
    setRoster(updatedRoster);
  }, [roster, currentSlotIndex]);
  
  const handleSelectProp = useCallback((prop, side) => {
    const updatedRoster = roster.map((s, i) => 
      i === currentSlotIndex 
        ? { ...s, prop, side, units: s.units || 10 }  // Default to 10 units if not set
        : s
    );
    setRoster(updatedRoster);
  }, [roster, currentSlotIndex]);
  
  const handleUnitsChange = useCallback((units) => {
    const updatedRoster = roster.map((s, i) => 
      i === currentSlotIndex 
        ? { ...s, units }
        : s
    );
    setRoster(updatedRoster);
  }, [roster, currentSlotIndex]);
  
  const handleClearSlot = useCallback(() => {
    const updatedRoster = roster.map((s, i) => 
      i === currentSlotIndex 
        ? { ...s, player: null, prop: null, side: null, units: 0 }
        : s
    );
    setRoster(updatedRoster);
  }, [roster, currentSlotIndex]);
  
  const handleComplete = useCallback(async () => {
    if (!allComplete) return;
    
    setSaving(true);
    
    // First ensure final save
    await doSave(roster);
    
    // Then call complete endpoint
    if (completeEntry) {
      try {
        const result = await completeEntry();
        if (result?.success) {
          showToast?.('Roster completed!', 'success');
          onSuccess?.();
        } else {
          const errorMsg = result?.errors?.[0]?.message || 'Failed to complete';
          showToast?.(errorMsg, 'error');
        }
      } catch (err) {
        showToast?.('Failed to complete roster', 'error');
      }
    } else if (lockEntry) {
      // Fallback to lock if complete not available
      try {
        const result = await lockEntry();
        if (result?.success) {
          showToast?.('Roster locked!', 'success');
          onSuccess?.();
        } else {
          const errorMsg = result?.errors?.[0]?.message || 'Failed to lock';
          showToast?.(errorMsg, 'error');
        }
      } catch (err) {
        showToast?.('Failed to lock roster', 'error');
      }
    }
    
    setSaving(false);
  }, [allComplete, roster, doSave, completeEntry, lockEntry, showToast, onSuccess]);
  
  const handleNext = useCallback(() => {
    if (currentSlotIndex < roster.length - 1) {
      handleSlotNavigation(currentSlotIndex + 1);
    }
  }, [currentSlotIndex, roster.length, handleSlotNavigation]);
  
  const handlePrev = useCallback(() => {
    if (currentSlotIndex > 0) {
      handleSlotNavigation(currentSlotIndex - 1);
    }
  }, [currentSlotIndex, handleSlotNavigation]);
  
  // ===== LOADING/ERROR =====
  
  if (slateLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-indigo-600 font-medium">Loading players...</p>
        </div>
      </div>
    );
  }
  
  if (slateError) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-500 font-bold mb-2">Failed to Load</p>
          <p className="text-slate-600 text-sm">{slateError}</p>
          <button onClick={onBack} className="mt-4 px-4 py-2 bg-slate-100 rounded-lg font-medium">
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // ===== MAIN RENDER =====
  
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-600">
          <ChevronLeft size={20} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-lg font-bold text-slate-900">Build Roster</h1>
        <div className="w-16" /> {/* Spacer */}
      </div>
      
      {/* Progress */}
      <ProgressHeader 
        completedSlots={completedSlots}
        totalSlots={requiredSlots}
        usedUnits={usedUnits}
        requiredUnits={requiredUnits}
      />
      
      {/* Save error indicator */}
      {saveError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-sm text-red-600">{saveError}</span>
        </div>
      )}
      
      {/* Slot navigation */}
      <SlotNavDots 
        roster={roster}
        currentIndex={currentSlotIndex}
        onNavigate={handleSlotNavigation}
      />
      
      {/* Current slot builder */}
      <div className="flex-1 overflow-hidden">
        <SlotBuilder
          slot={currentSlot}
          players={filteredPlayers}
          filters={filters}
          onFiltersChange={setFilters}
          onSelectPlayer={handleSelectPlayer}
          onSelectProp={handleSelectProp}
          onUnitsChange={handleUnitsChange}
          onClear={handleClearSlot}
          maxUnits={maxUnitsForSlot}
        />
      </div>
      
      {/* Navigation arrows */}
      <div className="bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between">
        <button 
          onClick={handlePrev}
          disabled={currentSlotIndex === 0}
          className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium
            ${currentSlotIndex === 0 
              ? 'text-slate-300 cursor-not-allowed' 
              : 'text-slate-600 bg-slate-100'
            }`}
        >
          <ChevronLeft size={18} /> Prev
        </button>
        
        {allComplete ? (
          <button
            onClick={handleComplete}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white rounded-lg font-semibold shadow-lg"
          >
            {saving ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Lock size={18} />
            )}
            Complete
          </button>
        ) : (
          <div className="text-xs text-slate-400 text-center">
            {completedSlots}/{requiredSlots} slots<br/>
            {usedUnits}/{requiredUnits} units
          </div>
        )}
        
        <button 
          onClick={handleNext}
          disabled={currentSlotIndex === roster.length - 1}
          className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium
            ${currentSlotIndex === roster.length - 1 
              ? 'text-slate-300 cursor-not-allowed' 
              : 'text-slate-600 bg-slate-100'
            }`}
        >
          Next <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default TunnelBuilder;
